import { DownloadIcon, PlayIcon } from '@radix-ui/react-icons';
import { Box, Button, Container, Flex, Spinner } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateInput } from 'components/common/form/date';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import {
  addWeeks,
  endOfToday,
  isValid,
  lightFormat,
  startOfToday,
} from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { RegexFilter } from 'interfaces/forms/text';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { isInteger } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { LOCAL_DATE_FORMAT, LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import {
  IRepeatabilityFilter,
  IRepeatabilityImage,
} from 'lib_ts/interfaces/modelling/i-repeatability';
import { first } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import {
  AdminMachinesService,
  IRecentSession,
} from 'services/admin/machines.service';
import { MainService } from 'services/main.service';
import slugify from 'slugify';

const sessionLabelFn = (m: IRecentSession): string => {
  const ds = lightFormat(new Date(m.max_created), LOCAL_DATE_FORMAT);
  return `*${m._id.substring(m._id.length - 7)} @ ${ds} (${m.total} fires)`;
};

interface IProps {
  machine: IMachine;
}

enum ShotType {
  Training = 'training',
  BP = 'bp',
}

export const PlottingTab = ({ machine }: IProps) => {
  const [start, setStart] = useState<Date>(addWeeks(startOfToday(), -2));
  const [end, setEnd] = useState<Date>(new Date());
  const [selectedSessionIDs, setSelectedSessionIDs] = useState<string[]>([]);
  const [recentSessions, setRecentSessions] = useState<IRecentSession[]>([]);
  const [filter, setFilter] = useState(() => {
    const output: IRepeatabilityFilter = {
      machineID: machine.machineID,
      // will be updated when start changes
      start_date: addWeeks(startOfToday(), -2).toISOString(),
      // will be updated when end changes
      end_date: new Date().toISOString(),
      ball_type: machine.ball_type,
      min_group_size: 3,
      min_groups: 1,
      center_groups: true,
      training: null,
      limit: 10000,
      sessions: [],
      pitch_list_ids: [],
      pitch_type: undefined,
      exclude_low_confidence: false,
      exclude_inactive: true,
      check_confidence: false,
      check_spin: false,
      check_breaks: false,
      split_by_tracking_device: false,
      build_images: true,
    };

    return output;
  });
  const [images, setImages] = useState<IRepeatabilityImage[]>([]);
  const [summaries, setRepeatabilitySummaries] = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);
  const [loadingRecentSessions, setLoadingRecentSessions] = useState(false);

  useEffect(() => {
    const getRecentSessions = async () => {
      setLoadingRecentSessions(true);

      const result = await AdminMachinesService.getInstance().getRecentSessions(
        machine.machineID
      );

      setRecentSessions(result);
      setLoadingRecentSessions(false);
    };

    getRecentSessions();
  }, [machine.machineID]);

  const validateFilter = (filter: IRepeatabilityFilter): boolean => {
    try {
      const reasons: string[] = [];

      if (!filter.start_date || !isValid(new Date(filter.start_date))) {
        reasons.push('Start Date');
      }

      if (!filter.end_date || !isValid(new Date(filter.end_date))) {
        reasons.push('End Date');
      }

      if (!isInteger(filter.min_group_size)) {
        reasons.push('Min Group Size');
      }

      if (!isInteger(filter.min_groups)) {
        reasons.push('Min Groups');
      }

      if (!isInteger(filter.limit)) {
        reasons.push('Record Limit');
      }

      if (reasons.length > 0) {
        NotifyHelper.warning({
          message_md: `Invalid field(s): ${reasons.join(', ')}`,
        });
        return false;
      }

      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const generate = async () => {
    const safeFilter = { ...filter };

    if (selectedSessionIDs.length > 0) {
      safeFilter.sessions = ArrayHelper.unique(selectedSessionIDs);
    }

    safeFilter.pitch_list_ids = ArrayHelper.unique(
      safeFilter.pitch_list_ids.map((id) => id.trim())
    );

    if (!validateFilter(safeFilter)) {
      return;
    }

    setGenerating(true);
    setImages([]);

    NotifyHelper.warning({
      message_md:
        'Fetching repeatability report, please wait as this may take a while...',
      delay_ms: 10_000,
    });

    const results =
      await AdminMachinesService.getInstance().getRepeatabilityImages(
        safeFilter
      );

    setImages(results.success ? results.images : []);
    setRepeatabilitySummaries(results.success ? results.summaries : []);
    setGenerating(false);
  };

  const getFullFilename = (filename: string, extension: string): string => {
    const components: string[] = [filter.machineID ?? 'anonymous'];

    if (filter.start_date) {
      const sDate = new Date(filter.start_date);
      if (isValid(sDate)) {
        components.push(
          format(sDate, 'yyyy-MM-dd', { timeZone: LOCAL_TIMEZONE })
        );
      }
    }

    if (filter.end_date) {
      const eDate = new Date(filter.end_date ?? '');
      if (isValid(eDate)) {
        components.push(
          format(eDate, 'yyyy-MM-dd', { timeZone: LOCAL_TIMEZONE })
        );
      }
    }

    if (filter.ball_type) {
      components.push(filter.ball_type);
    }

    if (filter.pitch_type) {
      components.push(filter.pitch_type);
    }

    if (filter.training) {
      components.push('training');
    }

    if (filter.center_groups) {
      components.push('center');
    }

    components.push(filename);

    const slug = slugify(components.join(' '), {
      replacement: '_',
      trim: true,
    });

    return `${slug}.${extension}`;
  };

  const saveImage = async (img: IRepeatabilityImage) => {
    const filename = getFullFilename(img.filename, img.extension);

    const url = `data:image/png;base64, ${img.base64}`;
    const res = await fetch(url);
    const blob = await res.blob();

    MiscHelper.saveAs(blob, filename);
  };

  const trainingOptions: IOption[] = [
    {
      label: 'Training',
      value: ShotType.Training,
    },
    {
      label: 'BP Shots',
      value: ShotType.BP,
    },
  ];

  const trainingValues = useMemo(() => {
    switch (filter.training) {
      case true: {
        // only training shots
        return [ShotType.Training];
      }

      case false: {
        // only NOT training shots
        return [ShotType.BP];
      }

      case undefined:
      default: {
        // i.e. skip filtering
        return Object.values(ShotType);
      }
    }
  }, [filter.training]);

  const controls = (
    <CommonFormGrid columns={3}>
      {/* row 1 */}
      <CommonDateInput
        id="performance-plot-start"
        label="common.start-date"
        disabled={generating}
        defaultValue={start}
        maxDate={end}
        onChange={(date) => {
          if (!date) {
            NotifyHelper.warning({
              message_md: t('common.check-inputs-msg'),
            });
            return;
          }

          setStart(date);
          setFilter((prev) => ({
            ...prev,
            start_date: date.toISOString(),
          }));
        }}
        showTime
      />
      <CommonDateInput
        id="performance-plot-end"
        label="common.end-date"
        disabled={generating}
        defaultValue={end}
        minDate={start}
        maxDate={endOfToday()}
        onChange={(date) => {
          if (!date) {
            NotifyHelper.warning({
              message_md: t('common.check-inputs-msg'),
            });
            return;
          }

          setEnd(date);
          setFilter((prev) => ({
            ...prev,
            end_date: date.toISOString(),
          }));
        }}
        showTime
      />
      <CommonSelectInput
        id="performance-plot-ball"
        name="ball_type"
        label="common.ball-type"
        disabled={generating}
        options={(machine.ball_type_options ?? [BallType.MLB]).map((t) => ({
          label: t,
          value: t,
        }))}
        value={filter.ball_type}
        onChange={(v) =>
          setFilter((prev) => ({ ...prev, ball_type: v as BallType }))
        }
      />

      {/* row 2 */}
      <CommonSearchInput
        id="training"
        label="Training"
        name="training"
        values={trainingValues}
        onChange={(values) => {
          if (Object.values(ShotType).every((v) => values.includes(v))) {
            setFilter((prev) => ({
              ...prev,
              training: null,
            }));
            return;
          }

          switch (first(values) as ShotType) {
            case ShotType.Training: {
              setFilter((prev) => ({
                ...prev,
                training: true,
              }));
              break;
            }
            case ShotType.BP: {
              setFilter((prev) => ({
                ...prev,
                training: false,
              }));
              break;
            }
            default: {
              break;
            }
          }
        }}
        options={trainingOptions}
        disabled={generating}
        hint_md="Filter by training or BP data only"
        maxDisplayItems={2}
        multiple
        hideSearch
      />
      <CommonTextInput
        id="performance-plot-min-group"
        label="Min Group Size"
        type="number"
        name="min_group_size"
        disabled={generating}
        value={filter.min_group_size?.toString()}
        placeholder="Type an integer value"
        onNumericChange={(v) => {
          setFilter((prev) => ({
            ...prev,
            min_group_size: Math.round(v),
          }));
        }}
        hint_md="Minimum number of shots per target machine state"
      />
      <CommonSelectInput
        id="performance-plot-confidence"
        label="Exclude Low Confidence"
        name="confidence"
        disabled={generating}
        options={[
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ]}
        value={filter.exclude_low_confidence.toString()}
        onBooleanChange={(v) => {
          setFilter((prev) => ({
            ...prev,
            exclude_low_confidence: v,
          }));
        }}
        hint_md="Filter out samples with bad tracking device data"
      />

      {/* row 3 */}
      <CommonTextInput
        id="collectionID"
        label="Collection ID"
        name="collectionID"
        disabled={generating}
        value={filter.collectionID}
        onChange={(v) =>
          setFilter((prev) => ({
            ...prev,
            collectionID: v,
          }))
        }
        hint_md='Like "AAAAAAAA"'
        optional
      />
      <CommonSearchInput
        id="sessions"
        label="Session IDs"
        labelIcon={loadingRecentSessions ? <Spinner /> : undefined}
        disabled={generating || loadingRecentSessions}
        options={recentSessions.map((o) => ({
          label: sessionLabelFn(o),
          value: o._id,
        }))}
        values={selectedSessionIDs}
        onChange={(ids) => {
          setSelectedSessionIDs(ids);
        }}
        hint_md="Only sessions with fires are listed, with most recent ones sorted to the top"
        multiple
        optional
        // to preserve most recent first
        skipSort
      />
      <CommonTextInput
        id="performance-plot-pitch-list-ids"
        label="Pitch List IDs"
        name="pitch_list_ids"
        disabled={generating}
        placeholder={`(${t('common.comma-delimited')})`}
        regex={RegexFilter.Whitespace}
        value={filter.pitch_list_ids.join(',')}
        onChange={(v) => {
          setFilter((prev) => ({
            ...prev,
            pitch_list_ids: (v ?? '').split(','),
          }));
        }}
        optional
      />
      {/* row 4 */}
      <CommonSelectInput
        id="performance-plot-inactive"
        label="Exclude Inactive"
        name="inactive"
        disabled={generating}
        options={[
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ]}
        value={filter.exclude_inactive.toString()}
        onBooleanChange={(v) => {
          setFilter((prev) => ({
            ...prev,
            exclude_inactive: v,
          }));
        }}
        hint_md="Filter out samples with old matching hashes"
      />
      <CommonSelectInput
        id="performance-plot-aiming"
        label="Include Aiming Shots"
        name="aiming"
        disabled={generating}
        options={[
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ]}
        value={filter.aiming?.toString()}
        onOptionalBooleanChange={(v) => {
          setFilter((prev) => ({
            ...prev,
            aiming: v,
          }));
        }}
        hint_md="Filter samples based on whether they were used for aiming or not"
        optional
      />
      <CommonSelectInput
        id="performance-plot-split-by-tracking-device"
        label="Split By Tracking Device"
        name="split_by_tracking_device"
        disabled={generating}
        options={[
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ]}
        value={filter.split_by_tracking_device.toString()}
        onBooleanChange={(v) => {
          setFilter((prev) => ({
            ...prev,
            split_by_tracking_device: v,
          }));
        }}
        hint_md="Show reports for all active tracking devices"
      />
    </CommonFormGrid>
  );

  const renderImages = () => (
    <Container size="4">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        {images.map((img, index) => (
          <Box key={`image-${index}`}>
            <img
              src={`data:image/png;base64, ${img.base64}`}
              style={{ width: '100%', height: 'auto' }}
              title="Click to save the graph as an image"
              alt={img.filename}
              className="cursor-pointer"
              onClick={() => saveImage(img)}
            />
          </Box>
        ))}
      </Flex>
    </Container>
  );

  return (
    <ErrorBoundary identifier="PlottingTab">
      <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
        {controls}

        <CommonCallout
          color={RADIX.COLOR.NEUTRAL}
          text="Will generate 5 report images: Plate Variance, Break Variance, Break Error, BS Variance, and BS Error. Click on each image to save it."
        />

        <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
          <Button
            size={RADIX.BUTTON.SIZE.SM}
            color={RADIX.COLOR.SUCCESS}
            disabled={generating}
            onClick={generate}
            loading={generating}
          >
            <PlayIcon /> Generate
          </Button>

          <Box flexGrow="1">&nbsp;</Box>

          {images.length > 0 && (
            <Button
              size={RADIX.BUTTON.SIZE.SM}
              color={RADIX.COLOR.INFO}
              disabled={generating}
              onClick={() => images.forEach((img) => saveImage(img))}
            >
              <DownloadIcon /> Download Images
            </Button>
          )}

          {summaries.length > 0 && (
            <Button
              size={RADIX.BUTTON.SIZE.SM}
              color={RADIX.COLOR.INFO}
              disabled={generating}
              onClick={async () => {
                const csvString =
                  await MainService.getInstance().convertJSONToCSV(summaries);

                const blob = new Blob([csvString], { type: 'text/csv' });
                MiscHelper.saveAs(
                  blob,
                  `${machine.machineID}-repeatability-summaries.csv`
                );
              }}
            >
              <DownloadIcon /> Download Summary
            </Button>
          )}
        </Flex>

        {renderImages()}
      </Flex>
    </ErrorBoundary>
  );
};
